// client/src/components/NotificationBell.js
import React, { useEffect, useState, useRef } from "react";
import { io } from "socket.io-client";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

/*
  NotificationBell.js — Final consolidated version (Option A)
  -----------------------------------------------------------
  - Preserves original behavior (socket, dedupe, sound, mark-read, clear-all, hero modal).
  - Robust detection of missing/deleted resources (works with your backend's varied responses).
  - Disabled notifications:
      * Marked visually (yellowish) in both light and dark themes.
      * Do NOT navigate on click.
      * Show a delete/dustbin button on hover which deletes that notification only.
  - Delete handling:
      * If notification._id startsWith "notif_local_" => delete only client-side (no server call).
      * Otherwise call DELETE /api/notifications/:id gracefully and update UI.
  - Chat redirect fixed: verify resource first, then navigate + reload.
  - postType "post" will redirect to /task/:id (Option A as requested).
  - Glass magnifier effect: polished for both light (bluish) and dark themes.
  - Defensive: does not crash when server returns text/html or weird bodies.
  - This file is verbose with comments for clarity and future merge-ease.
*/

const SOCKET_URL = process.env.REACT_APP_API_URL; // change if server root differs

// ----------------------------- Utilities -----------------------------

// Check 24-character hex mongo ObjectId
function isValidObjectId(id) {
  return typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id);
}

// Create dedupe key (prefer real _id)
function notifKey(n) {
  if (!n) return "";
  if (n._id && isValidObjectId(String(n._id))) return String(n._id);
  return `${(n.text || "").slice(0, 120)}::${new Date(
    n.createdAt || n.createdAt === 0 ? n.createdAt : Date.now()
  ).toISOString()}`;
}

// Subtitle text helper
const subtitleText = (n) =>
  n.subtitle ||
  (n.fromUser && (typeof n.fromUser === "string" ? n.fromUser : n.fromUser.name)) ||
  (n.postTitle || n.title || "");

// Try to parse JSON, fallback to text
async function safeParseResponse(res) {
  // Accepts a fetch Response, returns { ok, status, json: maybeObject, text: string }
  let text = "";
  let json = null;
  try {
    // read as text first (so we can inspect)
    text = await res.text();
    try {
      json = text ? JSON.parse(text) : null;
    } catch (e) {
      json = null;
    }
  } catch (err) {
    text = "";
    json = null;
  }
  return { ok: res.ok, status: res.status, json, text };
}

// Determine if response indicates a missing resource (robust)
/*
  This function is the heart of detection:
  - If status is 200 and body has helpful data -> we treat as present.
  - If status is 404 -> missing.
  - If status is 400/422/500 -> missing (server-side error usually).
  - If body text contains "Cannot GET" or "Invalid id" or "Task not found" or "Server error" or "CastError" -> missing.
  - If res.ok === true but JSON is { error: '...' } and message contains 'not found' or 'Invalid id' -> missing.
  - This models the exact behaviour you reported from your backend.
*/
function responseIndicatesMissing(parsed) {
  if (!parsed) return true;
  const { ok, status, json, text } = parsed;

  // If 404 -> definitely missing
  if (status === 404) return true;

  // If server error -> treat as missing (we avoid navigating to broken pages)
  if (status >= 500) return true;

  // If badly formed id or validation returned 400/422 treat as missing
  if (status >= 400 && status < 500) {
    // some endpoints return 200 but JSON { error: "Invalid id" } — we'll check below
    // but if it's 400 explicitly, treat as missing
    return true;
  }


  // 🔥 NEW FIX: completed task ko dead treat karo
  if (json && typeof json === "object") {
    const task = json.task || json;

    if (task && task.status === "completed") {
      return true; // resource exists but SHOULD BE DISABLED
    }
  }

  // If response contains obvious server text for missing
  const txt = (text || "").toLowerCase();
  if (
    txt.includes("cannot get") ||
    txt.includes("invalid id") ||
    txt.includes("task not found") ||
    txt.includes("server error") ||
    txt.includes("casterror") ||
    txt.includes("not found")
  ) {
    return true;
  }

  // If JSON contains { error: "..." } and message includes not found / invalid
  if (json && typeof json === "object" && json.error && typeof json.error === "string") {
    const e = json.error.toLowerCase();
    if (e.includes("not found") || e.includes("invalid") || e.includes("server error") || e.includes("task not found")) {
      return true;
    }
    // even if error exists but doesn't match, treat as missing for safety if status not ok
    if (!ok) return true;
  }

  // If ok and there's data — treat as present
  if (ok) return false;

  // Fallback: treat as missing
  return true;
}

// ----------------------------- Component -----------------------------
export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // UI state
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]); // newest first
  const [unread, setUnread] = useState(0);

  // hero modal
  const [hero, setHero] = useState({ open: false, message: "" });

  // refs
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);
  const socketRef = useRef(null);
  const audioRef = useRef(null);
  const mountedRef = useRef(true);

  // preload sound
  useEffect(() => {
    audioRef.current = new Audio("/notify.mp3");
    audioRef.current.preload = "auto";
  }, []);

  // outside click closes dropdown
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        bellRef.current &&
        !bellRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  // mountedRef
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ----------------------------- Add Notification (dedupe + sound) -----------------------------
  const addNotification = (n) => {
    if (!n) return;

    // friendly text fallback
    const textCandidates = [n.text, n.message, (n.fromUser && n.fromUser.name), ""].filter(Boolean);
    let text = textCandidates[0] || "You have a new notification";

    if (!n.text && n.message) text = n.message;

    if (!n.text && n.type && String(n.type).toLowerCase().includes("chat")) {
      text = text || "New chat message";
    }

    let createdAt = n.createdAt ? new Date(n.createdAt) : new Date();

    const normalized = {
      ...n,
      _id: n._id || n.id || `notif_local_${Date.now()}`,
      text,
      createdAt: createdAt.toISOString ? createdAt.toISOString() : new Date().toISOString(),
      subtitle: n.subtitle || n.postTitle || n.postName || "",
      hero: n.hero || false,
      heroMessage: n.heroMessage || "",
      disabled: !!n.disabled, // may be flagged by checks
    };

    const key = notifKey(normalized);
    setList((prev) => {
      const seen = new Set(prev.map((p) => notifKey(p)));
      if (seen.has(key)) {
        const existingIndex = prev.findIndex((p) => notifKey(p) === key);
        const existing = prev[existingIndex];

        // merge local->server id
        if (
          existing &&
          existing._id &&
          String(existing._id).startsWith("notif_local_") &&
          normalized._id &&
          isValidObjectId(String(normalized._id))
        ) {
          const copy = [...prev];
          copy[existingIndex] = { ...existing, ...normalized };
          return copy;
        }
        return prev;
      }
      return [normalized, ...prev];
    });

    setUnread((u) => u + 1);

    // play sound (non-blocking)
    try {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch (e) {
      // ignore
    }
  };

  // ----------------------------- Check resource existence (robust) -----------------------------
  const checkResourceExists = async (postId, guessedType = "task") => {
    // returns true if resource exists, false otherwise
    if (!postId || !isValidObjectId(String(postId))) return false;

    const token = localStorage.getItem("cp_token");
    const headers = { Authorization: `Bearer ${token}` };

    // Prepare candidate URLs based on guessedType
    let tryUrls = [];

    if (guessedType === "task") {
      tryUrls = [`${SOCKET_URL}/api/tasks/${postId}`];
    } else if (guessedType === "recruit") {
      tryUrls = [`${SOCKET_URL}/api/opportunities/${postId}`];
    } else if (guessedType === "lost") {
      tryUrls = [`${SOCKET_URL}/api/lost-found/${postId}`];
    } else if (guessedType === "post") {
      // you asked to treat "post" as task (Option A), but we still check posts endpoint if available
      tryUrls = [`${SOCKET_URL}/api/tasks/${postId}`, `${SOCKET_URL}/api/posts/${postId}`];
    } else {
      // fallback try all known endpoints
      tryUrls = [
        `${SOCKET_URL}/api/tasks/${postId}`,
        `${SOCKET_URL}/api/opportunities/${postId}`,
        `${SOCKET_URL}/api/lost-found/${postId}`,
        `${SOCKET_URL}/api/posts/${postId}`,
      ];
    }

    // iterate and try to find a positive response
    for (const url of tryUrls) {
      try {
        const res = await fetch(url, { headers });
        // we will parse safely
        const parsed = await safeParseResponse(res);
        const isMissing = responseIndicatesMissing(parsed);
        if (!isMissing) {
          // found a valid resource
          return true;
        }
        // otherwise continue trying other endpoints
      } catch (err) {
        // network/other error -> try next URL
        continue;
      }
    }
    // All endpoints failed to indicate existence
    return false;
  };

  // ----------------------------- Initial load: fetch notifications & verify each -----------------------------
  useEffect(() => {
    if (!user) {
      setList([]);
      setUnread(0);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const token = localStorage.getItem("cp_token");
        const res = await fetch(`${SOCKET_URL}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // parse response
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          console.error("Failed to load notifications", data);
          // set empty or keep existing? We'll set empty to avoid stale state
          if (!cancelled && mountedRef.current) {
            setList([]);
            setUnread(0);
          }
          return;
        }

        const notifs = Array.isArray(data.notifications) ? data.notifications : [];

        // normalize & initial disabled=false
        const normalized = notifs.map((n) => {
          const createdAt = n.createdAt ? new Date(n.createdAt) : new Date();
          return {
            ...n,
            _id: n._id || n.id,
            text: n.text || n.message || "You have a notification",
            createdAt: createdAt.toISOString(),
            subtitle: n.subtitle || "",
            hero: n.hero || false,
            heroMessage: n.heroMessage || "",
            disabled: false,
          };
        });

        // dedupe preserving order
        const uniq = [];
        const seen = new Set();
        for (const n of normalized) {
          const k = notifKey(n);
          if (!seen.has(k)) {
            seen.add(k);
            uniq.push(n);
          }
        }

        if (cancelled || !mountedRef.current) return;

        // For each notification we'll try to verify its resource (rate-friendly).
        const checks = uniq.map(async (n) => {
          let postId = null;
          if (n.postId) postId = n.postId;
          if (!postId && n.task) postId = n.task;
          if (!postId && n.post) postId = n.post;

          // guess type
          let guessed = null;
          if (n.postType) guessed = String(n.postType).toLowerCase();
          if (!guessed && n.chatType) guessed = String(n.chatType).toLowerCase();
          if (!guessed && n.type) guessed = String(n.type).toLowerCase();

          const mapGuess = (g) => {
            if (!g) return null;
            if (g.includes("opportunity") || g.includes("recruit") || g.includes("club")) return "recruit";
            if (g.includes("lost") || g.includes("found") || g.includes("claim")) return "lost";
            if (g.includes("post")) return "post";
            if (g.includes("task")) return "task";
            return null;
          };

          const mapped = mapGuess(guessed);

          if (!postId || !isValidObjectId(String(postId))) {
            // nothing to verify
            return { _id: n._id, disabled: false };
          }

          const ok = await checkResourceExists(postId, mapped || "task");
          return { _id: n._id, disabled: ok === "completed" || ok === false };
        });

        // wait all checks
        const results = await Promise.all(checks);
        const disabledMap = new Map(results.map((r) => [r._id, r.disabled]));

        const final = uniq.map((n) => ({ ...n, disabled: !!disabledMap.get(n._id) }));
        if (!mountedRef.current) return;
        setList(final);
        setUnread(final.filter((x) => !x.isRead).length);

        // show hero if any
        const heroNotif = final.find((x) => x.hero);
        if (heroNotif) setHero({ open: true, message: heroNotif.heroMessage || heroNotif.text });
      } catch (err) {
        console.error("Notif load failed", err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ----------------------------- Socket setup (incoming notifications) -----------------------------
  useEffect(() => {
    if (!user) return;

    socketRef.current = io(SOCKET_URL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 2000,
    });

    socketRef.current.on("connect", () => {
      if (user?.id) socketRef.current.emit("auth", { userId: user.id });
    });

    socketRef.current.on("notification", async (payload) => {
      try {
        if (!payload) return;

        // hero notifications: show hero modal and still add
        if (payload && payload.hero) {
          addNotification(payload);
          setHero({ open: true, message: payload.heroMessage || payload.text || "You're a hero!" });
          return;
        }

        // normalize
        const createdAt = payload.createdAt ? new Date(payload.createdAt) : new Date();
        const normalized = {
          ...payload,
          _id: payload._id || payload.id || `notif_local_${Date.now()}`,
          text: payload.text || payload.message || "You have a new notification",
          createdAt: createdAt.toISOString(),
          subtitle: payload.subtitle || payload.postTitle || "",
          hero: payload.hero || false,
          heroMessage: payload.heroMessage || "",
          disabled: false,
        };

        // try to check resource if it points to a post/task
        let postId = null;
        if (normalized.postId) postId = normalized.postId;
        if (!postId && normalized.task) postId = normalized.task;
        if (!postId && normalized.post) postId = normalized.post;

        let guessed = null;
        if (normalized.postType) guessed = String(normalized.postType).toLowerCase();
        if (!guessed && normalized.chatType) guessed = String(normalized.chatType).toLowerCase();
        if (!guessed && normalized.type) guessed = String(normalized.type).toLowerCase();

        const mapGuess = (g) => {
          if (!g) return null;
          if (g.includes("opportunity") || g.includes("recruit") || g.includes("club")) return "recruit";
          if (g.includes("lost") || g.includes("found") || g.includes("claim")) return "lost";
          if (g.includes("post")) return "post";
          if (g.includes("task")) return "task";
          return null;
        };

        const mapped = mapGuess(guessed);

        if (postId && isValidObjectId(String(postId))) {
          const ok = await checkResourceExists(postId, mapped || "task");
          normalized.disabled = !ok;
        } else {
          normalized.disabled = false;
        }

        addNotification(normalized);
      } catch (err) {
        console.error("Failed to process incoming notification", err);
      }
    });

    return () => {
      socketRef.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ----------------------------- Actions -----------------------------
  const markAllRead = async () => {
    try {
      const token = localStorage.getItem("cp_token");
      await fetch(`${SOCKET_URL}/api/notifications/mark-read`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ all: true }),
      });
    } catch (err) {
      console.error("mark-all-read failed", err);
    }
    setList((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnread(0);
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all notifications?")) return;
    try {
      const token = localStorage.getItem("cp_token");
      await fetch(`${SOCKET_URL}/api/notifications/clear`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setList([]);
      setUnread(0);
    } catch (err) {
      console.error("Clear failed", err);
    }
  };

  // Delete single notification (robust)
  const deleteNotification = async (notifId) => {
    if (!notifId) return;

    // If it's a local-only ID (temporary), simply remove from client without calling server
    if (String(notifId).startsWith("notif_local_")) {
      setList((prev) => prev.filter((p) => p._id !== notifId));
      return;
    }

    if (!window.confirm("Delete this notification?")) return;
    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${SOCKET_URL}/api/notifications/${notifId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      // try to parse json or text for helpful error
      const parsed = await safeParseResponse(res);
      if (!res.ok) {
        console.error("Server delete failed:", parsed);
        // alert("Failed to delete notification (server error).");
        return;
      }

      // success
      setList((prev) => prev.filter((p) => p._id !== notifId));
    } catch (err) {
      console.error("deleteNotification failed", err);
      // alert("Failed to delete notification (network error).");
    }
  };

  // Helper to filter valid IDs before sending mark-read
  const filterValidIds = (ids = []) => {
    if (!Array.isArray(ids)) return [];
    return ids.filter((id) => id && isValidObjectId(String(id)));
  };
  // 🔥 SINGLE SOURCE OF TRUTH FOR REDIRECTS
function getNotificationTarget(n, currentUserId) {
  if (!n || n.disabled) return null;

  // -------- TASK BASED --------
  const taskId = n.task || (n.postType === "task" ? n.postId : null);
  if (taskId && isValidObjectId(String(taskId))) {
    return { path: `/task/${taskId}` };
  }

  // -------- LOST & FOUND --------
  if (
    n.postType === "lost" &&
    n.postId &&
    isValidObjectId(String(n.postId))
  ) {
    return { path: `/lost-found/${n.postId}` };
  }

  // -------- RECRUIT / OPPORTUNITY --------
  if (
    (n.postType === "recruit" || n.postType === "opportunity") &&
    n.postId &&
    isValidObjectId(String(n.postId))
  ) {
    return { path: `/recruit/${n.postId}` };
  }

  // -------- CHAT (TASK / LOST / RECRUIT) --------
  if (n.type === "chat_message" || n.type === "group_message") {
    const postId = n.postId || n.task;
    if (!isValidObjectId(String(postId))) return null;

    let participant = n.participant;
    if (participant && typeof participant === "object") {
      participant = participant._id || participant.id;
    }

    if (!participant) participant = currentUserId;
    if (!isValidObjectId(String(participant))) return null;

    const chatType =
      n.chatType ||
      (n.postType === "recruit"
        ? "recruit"
        : n.postType === "lost"
        ? "lost"
        : "task");

    return { path: `/chat/${chatType}/${postId}/${participant}` };
  }

  return null;
}


  // infer chat type (original logic)
  const inferChatType = (notif) => {
    const t = (notif.type || "").toLowerCase();
    if (t.includes("opportunity") || t.includes("recruit") || t.includes("club")) return "recruit";
    if (t.includes("lost") || t.includes("found") || t.includes("claim")) return "lost";
    return "task";
  };

  // ----------------------------- Click handler -----------------------------
const handleClickNotification = async (n) => {
  if (!n || n.disabled) return;

  // mark read (UI only)
  setList((prev) =>
    prev.map((x) =>
      x._id === n._id ? { ...x, isRead: true } : x
    )
  );
  setUnread((u) => Math.max(0, u - 1));
  setOpen(false);

  // 🔥 NO FETCH, NO CHECK, NO RELOAD

  // derive postId
  const postId = n.postId || n.task || n.post || null;

  // derive participant
  let participant = n.participant || n.fromUser || null;
  if (participant && typeof participant === "object") {
    participant = participant._id || participant.id || null;
  }

  /* ---------- HERO (NO REDIRECT) ---------- */
  if (n.hero || n.type === "lost_success") {
    setHero({
      open: true,
      message: n.heroMessage || n.text || "You’re a hero 🤍",
    });
    return;
  }
  /* ---------- TASK RELATED NOTIFICATIONS ---------- */
  if (postId) {
    navigate(`/task/${postId}`);
    return;
  }



  /* ---------- LOST & FOUND ---------- */
  const lostTypes = [
    "lost_claim",
    "lost_owner_thinks_this_is_yours",
    "lost_delivered",
    "lost_delivered_wait_confirm",
  ];

  if (lostTypes.includes(n.type)) {
    if (!postId) return;
    navigate(`/lost-found/${postId}`);
    return;
  }


  /* ---------- DIRECT TASK ---------- */
  if (n.task) {
    navigate(`/task/${n.task}`);
    return;
  }

  /* ---------- GENERIC POST ---------- */
  if (postId) {
    if (n.postType === "recruit" || n.postType === "opportunity") {
      navigate(`/recruit/${postId}`);
    } else if (n.postType === "lost") {
      navigate(`/lost-found/${postId}`);
    } else {
      navigate(`/task/${postId}`);
    }
  }
};

  // ----------------------------- Render -----------------------------
  return (
    <div className="relative">
      {/* Bell */}
      <button
        ref={bellRef}
        onClick={() => {
          const opening = !open;
          setOpen(opening);
          if (opening) markAllRead();
        }}
        className="relative p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        title="Notifications"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-gray-700 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 
            2.032 0 0118 14.158V11a6 6 0 00-12 
            0v3.159c0 .538-.214 1.055-.595 
            1.436L4 17h5m6 0v1a3 3 0 11-6 
            0v-1m6 0H9" />
        </svg>

        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full shadow">
            {unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div ref={dropdownRef} className="absolute mt-3 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-3 z-50 animate-fadeIn left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:-right-2">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-lg font-semibold dark:text-white">Notifications</div>
              <div className="text-xs text-gray-400">{list.length} recent</div>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={markAllRead} className="text-sm text-blue-600 hover:underline">Mark all read</button>
              <button onClick={clearAll} className="text-sm text-red-600 hover:underline">Clear all</button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto custom-scrollbar">
            {list.length === 0 && (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-300">No notifications</div>
            )}

            {list.map((n) => {
              const disabled = !!n.disabled;
              return (
                <div
                  key={n._id}
                  onClick={() => {
                    // If disabled, main click does nothing
                    if (!disabled) handleClickNotification(n);
                  }}
                  className={`notif-item group p-3 mb-2 rounded-lg border cursor-pointer transition
                    ${disabled ? "bg-yellow-50 dark:bg-yellow-800/30 border-yellow-200 dark:border-yellow-700" : n.isRead ? "bg-white dark:bg-slate-700" : "bg-blue-50 dark:bg-slate-600"}
                    relative overflow-hidden`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-sm font-medium ${disabled ? "text-yellow-900 dark:text-yellow-300" : "text-gray-800 dark:text-white"} truncate`}
                        title={n.text}
                      >
                        {n.text}
                      </div>

                      <div className="text-xs mt-1 truncate" style={{ color: disabled ? "#6b4e00" : undefined }}>
                        {subtitleText(n)}
                      </div>
                    </div>

                    <div className="text-xs text-gray-400 whitespace-nowrap ml-2">
                      {new Date(n.createdAt).toLocaleString()}
                    </div>
                  </div>

                  {/* Disabled: show delete + label on hover (pointer-events handled) */}
                  {disabled && (
                    <div className="absolute inset-0 flex items-end justify-end p-3 pointer-events-none">
                      <div className="pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center gap-2">
                        <div className="text-xs text-yellow-700">Target removed</div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(n._id);
                          }}
                          className="px-2 py-1 text-xs bg-red-600 text-white rounded shadow"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Styling */}
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(-6px); }
              to { opacity: 1; transform: translateY(0); }
            }

            .custom-scrollbar::-webkit-scrollbar { width: 6px; }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 10px;
            }

            /* Magnifier / glass effect */
            .animate-fadeIn .notif-item {
              transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease;
              transform-origin: left center;
              will-change: transform, box-shadow;
              position: relative;
            }

            /* Light theme hover: bluish glass */
            .animate-fadeIn .notif-item:hover {
              transform: scale(1.035);
              z-index: 60;
              box-shadow: 0 14px 36px rgba(2,6,23,0.22);
              backdrop-filter: blur(6px) saturate(120%);
              background: linear-gradient(180deg, rgba(245, 250, 255, 0.92), rgba(235, 245, 255, 0.86));
              border-color: rgba(200,210,240,0.8);
            }

            /* Dark mode hover: darker glass */
            .dark .animate-fadeIn .notif-item:hover {
              background: linear-gradient(180deg, rgba(18,22,28,0.75), rgba(12,16,20,0.65));
              box-shadow: 0 12px 32px rgba(2,6,23,0.6);
            }

            /* Expand truncated text on hover */
            .animate-fadeIn .notif-item:hover .truncate {
              overflow: visible;
              white-space: normal;
            }

            /* Disabled visual treatments */
            .animate-fadeIn .notif-item.bg-yellow-50 {
              border-color: #f5d28a;
            }
            .dark .animate-fadeIn .notif-item.bg-yellow-800\\/30 {
              border-color: rgba(245,210,138,0.25);
            }

            /* pointer events for delete button */
            .notif-item .group:hover .pointer-events-auto {
              pointer-events: auto;
            }

            /* ensure transitions */
            .animate-fadeIn .notif-item .truncate {
              transition: all 160ms ease;
            }
          `}</style>
        </div>
      )}

      {/* HERO modal */}
      {hero.open && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 p-4">
          <div className="max-w-xl w-full bg-white dark:bg-slate-800 rounded-2xl p-8 text-center shadow-2xl">
            <div className="text-4xl font-extrabold text-indigo-600 dark:text-indigo-300 mb-4">You’re a hero! 🤍</div>
            <div className="text-lg text-slate-700 dark:text-slate-200 whitespace-pre-line">{hero.message}</div>

            <div className="mt-6">
              <button onClick={() => setHero({ open: false, message: "" })} className="px-6 py-2 bg-indigo-600 text-white rounded-lg">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
